import { useState, type ComponentProps, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Slider } from '@/ui/shadcn/slider'

interface IconSliderProps extends Omit<ComponentProps<typeof Slider>, 'value' | 'onValueChange' | 'onValueCommit'> {
  value: number
  startIcon: ReactNode
  endIcon: ReactNode
  formatValue?: (value: number) => string
  showZeroTick?: boolean
  onValueChange: (value: number) => void
  onValueCommit?: (value: number) => void
}

export function IconSlider({
  value,
  min = 0,
  max = 100,
  startIcon,
  endIcon,
  formatValue = String,
  showZeroTick = false,
  onValueChange,
  onValueCommit,
  disabled,
  className,
  ...props
}: IconSliderProps) {
  const [dragging, setDragging] = useState(false)
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) {
    throw new Error('Icon slider requires finite bounds with max greater than or equal to min')
  }
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new Error(`Icon slider value must be between ${min} and ${max}`)
  }
  const fixedRange = max === min
  const percent = fixedRange ? 0 : (value - min) / (max - min) * 100
  const zeroPercent = fixedRange ? 0 : (0 - min) / (max - min) * 100

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground opacity-55" aria-hidden="true">{startIcon}</span>
      <div className="relative min-w-0 flex-1 py-2">
        {dragging ? (
          <span
            className="pointer-events-none absolute -top-6 z-10 -translate-x-1/2 rounded-md bg-foreground px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-background shadow"
            style={{ left: `${percent}%` }}
            aria-hidden="true"
          >
            {formatValue(value)}
          </span>
        ) : null}
        {showZeroTick && zeroPercent > 0 && zeroPercent < 100 ? (
          <span className="pointer-events-none absolute left-0 top-1/2 z-[1] h-2.5 w-px -translate-y-1/2 bg-foreground/45" style={{ left: `${zeroPercent}%` }} aria-hidden="true" />
        ) : null}
        <Slider
          min={min}
          max={max}
          disabled={fixedRange || disabled}
          value={[value]}
          onValueChange={(values) => {
            const next = values[0]
            if (next === undefined) throw new Error('Icon slider did not provide a value')
            onValueChange(next)
          }}
          onValueCommit={(values) => {
            const next = values[0]
            if (next === undefined) throw new Error('Icon slider did not provide a committed value')
            setDragging(false)
            onValueCommit?.(next)
          }}
          onPointerDown={() => setDragging(true)}
          onPointerUp={() => setDragging(false)}
          onPointerCancel={() => setDragging(false)}
          onBlur={() => setDragging(false)}
          {...props}
        />
      </div>
      <span className="flex size-4 shrink-0 items-center justify-center text-foreground" aria-hidden="true">{endIcon}</span>
    </div>
  )
}
